/**
 * `/admin` — which kitchens players may pick, which invite codes are open,
 * which rooms are open, which rounds were recorded.
 *
 * One password, one cookie, four lists. It is deliberately a management page
 * and not a second editor: drawing happens at `/editor`, which is a link from
 * here, and everything on this page is a decision *about* a map rather than a
 * change *to* one. That keeps the destructive things — switching a kitchen off
 * mid-study, deleting one — in a place somebody has to go to on purpose.
 *
 * Two rules the page states rather than implies, because both look like bugs
 * when they are met without warning:
 *
 *   - a built-in kitchen can be hidden but never edited or deleted, so its
 *     row says "Built in" and its delete button is not there;
 *   - hiding a map takes it off the landing page and does nothing to a round
 *     already being played on it, which the room list beside it shows.
 *
 * No meaning is carried by colour. Every state is a word — "Shown to players",
 * "Hidden", "Playing", "Waiting for players" — and the one tinted panel (the
 * notice) says what happened in its sentence, as the editor's save notice does.
 *
 * The browser harness finds the controls by `data-testid`, not by their
 * wording, so the words here can change without breaking it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { OracleListEntry, SessionIndexEntry } from '@overcooked/shared'
import { TICK_MS, qualityOfRow } from '@overcooked/shared'
import { phaseWord, prettyMapName } from '../display'
import { adminOracleCell, anySolving, fetchOracleList, oracleByMap } from '../lobbyOracle'
import { oraclePathFor } from '../../oracle/path'
import type { BatchFilter } from '../../replay/sessionTabs'
import { NO_BATCH, NO_BATCH_LABEL, SOLO_TAB, TABS, batchLabel, batchOptions, inviteParam } from '../../replay/sessionTabs'
import type { InviteRow, MapRow, RoomRow } from './api'
import {
  consentLine,
  createInvite,
  deleteMap,
  fetchMaps,
  fetchRooms,
  fetchSessions,
  listInvites,
  logIn,
  logOut,
  openRoom,
  setInviteEnabled,
  setMapEnabled,
} from './api'

/** How often the live room list re-reads the server while the page is open. */
const ROOM_POLL_MS = 5000
/** How often the oracle column re-reads the cache while any kitchen is still being solved. */
const ORACLE_POLL_MS = 3000

type Gate =
  /** Nothing asked yet. */
  | 'checking'
  /** The cookie was accepted. */
  | 'in'
  /** No cookie, or it expired: show the password form. */
  | 'out'
  /** No `ADMIN_PASSWORD` on this server: a password form would be a loop. */
  | 'off'

type Notice = { tone: 'ok' | 'bad'; text: string }

const stamp = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'

const since = (at: number, now: number): string => {
  const seconds = Math.max(0, Math.round((now - at) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  return `${Math.floor(seconds / 3600)}h ago`
}

/** `1 player`, `2 players`. */
const count = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`

/** The replay tab's name for a study condition, so both pages say the same thing. */
const conditionName = (condition: SessionIndexEntry['condition']): string =>
  [...TABS, SOLO_TAB].find((tab) => tab.condition === condition)?.label ?? 'Unclassified'

/**
 * One line saying whether a round's data is whole, and if not, why.
 *
 * A round is never dropped for being incomplete — it is stored and labelled —
 * so this is where a researcher sees the label before using the round.
 */
function qualityLine(session: SessionIndexEntry): { flagged: boolean; text: string } {
  const quality = qualityOfRow(session, TICK_MS)
  const losses = quality.flags.filter((flag) => flag.startsWith('AUDIO_') || flag.startsWith('LOG_'))
  if (quality.audio === 'incomplete') return { flagged: true, text: `Audio incomplete: ${losses.join(', ')}` }
  if (quality.status === 'incomplete') return { flagged: true, text: `Data incomplete: ${losses.join(', ')}` }
  return { flagged: false, text: quality.audio === 'not_expected' ? 'No audio expected' : 'Audio complete' }
}

/** A room's batch as the list shows it. `null` is a room opened with the invite check off. */
const batchOf = (invite: string | null | undefined): string => batchLabel({ invite })

/**
 * The sessions picker's value for "every batch" — neither a code (codes have no
 * `*`) nor `''`, which is "No batch".
 */
const ALL_BATCHES = '*all*'

export default function AdminPage() {
  const [gate, setGate] = useState<Gate>('checking')
  const [password, setPassword] = useState('')
  const [notice, setNotice] = useState<Notice | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [maps, setMaps] = useState<MapRow[] | null>(null)
  const [invites, setInvites] = useState<InviteRow[] | null>(null)
  const [inviteCode, setInviteCode] = useState('')
  const [inviteNote, setInviteNote] = useState('')
  const [rooms, setRooms] = useState<RoomRow[] | null>(null)
  const [sessions, setSessions] = useState<SessionIndexEntry[] | null>(null)
  /** Show only the rounds whose data is not whole. */
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  // The batch the recorded rounds are narrowed to. Mirrored in a ref so the
  // one `refresh` every action ends with reads the current choice without
  // being rebuilt — and re-run — each time it changes.
  const [sessionBatch, setSessionBatch] = useState<BatchFilter>(undefined)
  const sessionBatchRef = useRef<BatchFilter>(undefined)
  const [now, setNow] = useState(() => Date.now())
  /**
   * The oracle column, from `GET /api/oracle` — the cache, never a solve.
   * Asked only once the map table has rendered, and again whenever it is
   * re-read (a save re-solves that kitchen), so the table never waits on it.
   */
  const [oracleRows, setOracleRows] = useState<OracleListEntry[] | null>(null)

  /**
   * Read all four lists, and let the first refusal decide the gate.
   *
   * The same four calls are the login check: there is no "am I logged in"
   * endpoint and there should not be one — the cookie is http-only, so the only
   * honest way to ask is to make the request the page wanted to make anyway.
   */
  const refresh = useCallback(async (): Promise<void> => {
    const [m, i, r, s] = await Promise.all([
      fetchMaps(),
      listInvites(),
      fetchRooms(),
      fetchSessions(fetch, { invite: sessionBatchRef.current }),
    ])
    for (const outcome of [m, i, r, s]) {
      if (outcome.ok) continue
      if (outcome.kind === 'unconfigured') {
        setGate('off')
        return
      }
      if (outcome.kind === 'unauthorised') {
        setGate('out')
        return
      }
    }
    setGate('in')
    if (m.ok) setMaps(m.value)
    if (i.ok) setInvites(i.value)
    if (r.ok) setRooms(r.value)
    if (s.ok) setSessions(s.value)
    // A list that failed for a reason other than the cookie keeps its old rows
    // and says so, rather than blanking the page over one bad response.
    const failed = [m, i, r, s].find((outcome) => !outcome.ok)
    if (failed && !failed.ok) setNotice({ tone: 'bad', text: failed.error })
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /**
   * The room list is the one thing here that changes without anybody pressing
   * anything, so it is the one thing that is polled. Maps and replays are
   * re-read after an action instead.
   */
  useEffect(() => {
    if (gate !== 'in') return
    const timer = setInterval(() => {
      setNow(Date.now())
      void fetchRooms().then((outcome) => {
        if (outcome.ok) setRooms(outcome.value)
        else if (outcome.kind === 'unauthorised') setGate('out')
      })
    }, ROOM_POLL_MS)
    return () => clearInterval(timer)
  }, [gate])

  useEffect(() => {
    if (gate !== 'in' || maps === null) return
    let cancelled = false
    void fetchOracleList().then((rows) => {
      if (!cancelled && rows) setOracleRows(rows)
    })
    return () => {
      cancelled = true
    }
  }, [gate, maps])

  // While something is still being solved, keep asking; stop once nothing is.
  const solving = anySolving(oracleRows)
  useEffect(() => {
    if (gate !== 'in' || !solving) return
    const timer = setInterval(() => {
      void fetchOracleList().then((rows) => {
        if (rows) setOracleRows(rows)
      })
    }, ORACLE_POLL_MS)
    return () => clearInterval(timer)
  }, [gate, solving])
  const oracleOf = useMemo(() => oracleByMap(oracleRows), [oracleRows])

  const submitPassword = useCallback(
    async (event: React.FormEvent): Promise<void> => {
      event.preventDefault()
      if (busy) return
      setBusy('login')
      setNotice(null)
      const result = await logIn(password)
      setBusy(null)
      if (!result.ok) {
        setNotice({ tone: 'bad', text: result.error })
        return
      }
      // Out of the component's hands from here: what keeps the page logged in
      // across a reload is the cookie the server just set, not anything held
      // in this state.
      setPassword('')
      await refresh()
    },
    [busy, password, refresh],
  )

  /** Every action is "do it, say what happened, re-read the lists". */
  const act = useCallback(
    async (key: string, run: () => Promise<Notice>): Promise<void> => {
      if (busy) return
      setBusy(key)
      setNotice(null)
      const said = await run()
      setBusy(null)
      setNotice(said)
      await refresh()
    },
    [busy, refresh],
  )

  const toggle = (map: MapRow): Promise<void> =>
    act(`switch:${map.name}`, async () => {
      const outcome = await setMapEnabled(map.name, !map.enabled)
      if (!outcome.ok) return { tone: 'bad', text: outcome.error }
      return {
        tone: 'ok',
        text: outcome.value.enabled
          ? `${prettyMapName(map.name)} is now shown to players.`
          : `${prettyMapName(map.name)} is now hidden. Games already using it keep going.`,
      }
    })

  const remove = (map: MapRow): Promise<void> =>
    act(`delete:${map.name}`, async () => {
      const outcome = await deleteMap(map.name)
      if (!outcome.ok) return { tone: 'bad', text: outcome.error }
      return { tone: 'ok', text: `${prettyMapName(map.name)} was deleted. Games already using it keep going.` }
    })

  const play = (map: MapRow): Promise<void> =>
    act(`play:${map.name}`, async () => {
      const outcome = await openRoom(map.name)
      if (!outcome.ok) return { tone: 'bad', text: outcome.error }
      window.location.href = `/r/${outcome.value.code}`
      return { tone: 'ok', text: `Opening game ${outcome.value.code}…` }
    })

  /**
   * Open a batch. The fields are cleared only when the server took the code, so
   * a refused one stays on screen to be corrected rather than typed again.
   */
  const openBatch = (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    return act('invite:create', async () => {
      const outcome = await createInvite(inviteCode, inviteNote)
      if (!outcome.ok) return { tone: 'bad', text: outcome.error }
      setInviteCode('')
      setInviteNote('')
      return {
        tone: 'ok',
        text: `${outcome.value.code} is open. Anyone with it can start a game.`,
      }
    })
  }

  const toggleInvite = (invite: InviteRow): Promise<void> =>
    act(`invite:${invite.code}`, async () => {
      const outcome = await setInviteEnabled(invite.code, !invite.enabled)
      if (!outcome.ok) return { tone: 'bad', text: outcome.error }
      return {
        tone: 'ok',
        text: outcome.value.enabled
          ? `${invite.code} is open again.`
          : `${invite.code} is closed. Games already started under it carry on.`,
      }
    })

  /**
   * Narrow the recorded rounds to one batch. Only the one list is re-read; the
   * address bar is left alone, because this page is a desk, not a link.
   */
  const chooseSessionBatch = (next: BatchFilter): void => {
    sessionBatchRef.current = next
    setSessionBatch(next)
    void fetchSessions(fetch, { invite: next }).then((outcome) => {
      // A slower answer to an older choice must not overwrite a newer one.
      if (sessionBatchRef.current !== next) return
      if (outcome.ok) setSessions(outcome.value)
      else if (outcome.kind === 'unauthorised') setGate('out')
      else setNotice({ tone: 'bad', text: outcome.error })
    })
  }

  // Every code an admin has opened, plus any a recorded round names that this
  // list does not (a code from another deploy's bucket), plus the one chosen.
  const sessionBatches = useMemo(
    () =>
      batchOptions(
        [...(invites ?? []).map((row) => ({ invite: row.code })), ...(sessions ?? [])],
        sessionBatch,
      ),
    [invites, sessions, sessionBatch],
  )

  const counts = useMemo(() => {
    const on = (maps ?? []).filter((map) => map.enabled).length
    return { on, total: maps?.length ?? 0 }
  }, [maps])

  const header = (
    <header className="bar">
      <span className="brand">
        Over<em>cooked</em>
      </span>
      <span className="bar-sub">Admin</span>
      <a className="bar-link" href="/editor">
        Kitchen editor
      </a>
      <a className="bar-link" href="/replay">
        Replays
      </a>
      <a className="bar-link" href="/admin/study">
        Study
      </a>
      <a className="bar-link" href="/">
        Back to the game
      </a>
    </header>
  )

  if (gate === 'checking') {
    return (
      <main className="page">
        {header}
        <p className="hint">Checking whether you are logged in…</p>
      </main>
    )
  }

  if (gate === 'off') {
    return (
      <main className="page">
        {header}
        <section className="admin-gate">
          <h1>Admin is turned off</h1>
          <p className="hint">
            Set <code>ADMIN_PASSWORD</code> (12 or more characters) on the server and restart it.
          </p>
        </section>
      </main>
    )
  }

  if (gate === 'out') {
    return (
      <main className="page">
        {header}
        <section className="admin-gate">
          <h1>Admin login</h1>
          <form className="admin-login" onSubmit={(event) => void submitPassword(event)}>
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              autoComplete="current-password"
              autoFocus
              onChange={(event) => setPassword(event.target.value)}
            />
            <button type="submit" disabled={busy !== null || password.length === 0}>
              {busy === 'login' ? 'Checking…' : 'Log in'}
            </button>
          </form>
          {notice && (
            <p className="hint" data-testid="admin-notice" data-tone={notice.tone} role="alert">
              {notice.text}
            </p>
          )}
          <p className="hint">You stay logged in on this browser for 12 hours.</p>
        </section>
      </main>
    )
  }

  return (
    <main className="page">
      {header}

      {notice && (
        <p className="hint admin-notice" data-testid="admin-notice" data-tone={notice.tone} role="status">
          {notice.text}
        </p>
      )}

      <section className="admin-layout">
        <article className="admin-panel">
          <h2>Kitchens</h2>
          <p className="hint">
            {counts.on} of {counts.total} shown to players. New kitchens start hidden.
          </p>

          <table className="admin-table admin-maps">
            <thead>
              <tr>
                <th scope="col">Kitchen</th>
                <th scope="col">Size</th>
                <th scope="col">Players</th>
                <th scope="col">Source</th>
                <th scope="col">Status</th>
                <th scope="col">Oracle</th>
                <th scope="col">Last saved</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(maps ?? []).map((map) => (
                <tr key={map.name} data-testid="map-row" data-map={map.name}>
                  <th scope="row">
                    {prettyMapName(map.name)}
                    <span className="admin-map-id">{map.name}</span>
                  </th>
                  <td className="mono">
                    {map.width}×{map.height}
                  </td>
                  <td className="mono">{map.seats}</td>
                  <td data-testid="map-source" data-preset={map.preset ? 'true' : 'false'}>
                    {map.preset ? 'Built in' : 'Saved from the editor'}
                  </td>
                  <td data-testid="map-status" data-enabled={map.enabled ? 'true' : 'false'}>
                    {map.enabled ? 'Shown to players' : 'Hidden'}
                  </td>
                  {(() => {
                    // The best score two coordinated chefs found here, at the
                    // full budget, with the budget beside it; or why not yet.
                    const cell = adminOracleCell(oracleOf.get(map.name) ?? (oracleRows === null ? undefined : { map: map.name, status: 'solving', budgetLabel: '' }))
                    return (
                      <td data-testid="map-oracle" data-status={cell.status} className="admin-oracle">
                        {cell.text}
                        {cell.label && <span className="admin-map-id">{cell.label}</span>}
                        {cell.watchable && (
                          <a href={oraclePathFor(map.name)} data-testid="map-oracle-watch">
                            Watch the oracle
                          </a>
                        )}
                      </td>
                    )
                  })()}
                  <td>{stamp(map.updatedAt)}</td>
                  <td className="admin-actions">
                    <button
                      type="button"
                      data-testid="map-toggle"
                      data-action={map.enabled ? 'hide' : 'show'}
                      onClick={() => void toggle(map)}
                      disabled={busy !== null}
                      title={
                        map.enabled
                          ? `Hide ${prettyMapName(map.name)} from players`
                          : `Show ${prettyMapName(map.name)} to players`
                      }
                    >
                      {busy === `switch:${map.name}` ? 'Saving…' : map.enabled ? 'Hide' : 'Show'}
                    </button>
                    <button type="button" onClick={() => void play(map)} disabled={busy !== null}>
                      {busy === `play:${map.name}` ? 'Opening…' : 'Open a game'}
                    </button>
                    {/* Absent rather than disabled for a preset: a button that
                        can never be pressed is a question the page keeps asking.
                        The "Source" column above says why. */}
                    {!map.preset && (
                      <button
                        type="button"
                        className="admin-danger"
                        data-testid="map-delete"
                        onClick={() => void remove(map)}
                        disabled={busy !== null}
                        title={`Delete ${prettyMapName(map.name)} permanently`}
                      >
                        {busy === `delete:${map.name}` ? 'Deleting…' : 'Delete'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {maps !== null && maps.length === 0 && (
                <tr>
                  <td colSpan={8} className="hint">
                    No kitchens found. Check the server logs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

        </article>

        <article className="admin-panel">
          <h2>Invite codes</h2>
          <p className="hint">
            One code per batch. Codes can be closed but not deleted. Use 3 to 24 letters, digits or
            hyphens.
          </p>

          <form className="admin-invite-form" onSubmit={(event) => void openBatch(event)}>
            <label htmlFor="admin-invite-code">Code</label>
            <input
              id="admin-invite-code"
              name="code"
              className="mono"
              value={inviteCode}
              placeholder="PILOT-A"
              autoComplete="off"
              spellCheck={false}
              maxLength={24}
              onChange={(event) => setInviteCode(event.target.value)}
            />
            <label htmlFor="admin-invite-note">Note</label>
            <input
              id="admin-invite-note"
              name="note"
              value={inviteNote}
              placeholder="Who this batch is for"
              autoComplete="off"
              maxLength={200}
              onChange={(event) => setInviteNote(event.target.value)}
            />
            <button type="submit" disabled={busy !== null || inviteCode.trim().length === 0}>
              {busy === 'invite:create' ? 'Adding…' : 'Add code'}
            </button>
          </form>

          {invites !== null && invites.length === 0 ? (
            <p className="hint" role="status">
              No invite codes yet.
            </p>
          ) : (
            <table className="admin-table admin-invites">
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Note</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(invites ?? []).map((invite) => (
                  <tr key={invite.code} data-testid="invite-row" data-invite={invite.code}>
                    <th scope="row" className="mono">
                      {invite.code}
                    </th>
                    <td data-testid="invite-note">{invite.note || '—'}</td>
                    <td data-testid="invite-status" data-enabled={invite.enabled ? 'true' : 'false'}>
                      {invite.enabled ? 'Open' : 'Closed'}
                    </td>
                    <td>{stamp(invite.createdAt)}</td>
                    <td className="admin-actions">
                      <button
                        type="button"
                        data-testid="invite-toggle"
                        data-action={invite.enabled ? 'disable' : 'enable'}
                        onClick={() => void toggleInvite(invite)}
                        disabled={busy !== null}
                        title={invite.enabled ? `Stop ${invite.code} opening new games` : `Let ${invite.code} open games again`}
                      >
                        {busy === `invite:${invite.code}` ? 'Saving…' : invite.enabled ? 'Close' : 'Reopen'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </article>

        <article className="admin-panel">
          <h2>Open games</h2>
          <p className="hint">Idle games close after 10 minutes.</p>

          {rooms !== null && rooms.length === 0 ? (
            <p className="hint" role="status">
              No games are open.
            </p>
          ) : (
            <ul className="admin-rooms">
              {(rooms ?? []).map((room) => (
                <li key={room.code}>
                  <span className="code">{room.code}</span>
                  <span className="admin-room-map">
                    {prettyMapName(room.mapName)} <span className="mono hint">{room.mapName}</span>
                  </span>
                  <span
                    className={room.invite ? 'admin-room-batch mono' : 'admin-room-batch hint'}
                    title="The invite code this game was opened under"
                  >
                    {batchOf(room.invite)}
                  </span>
                  <span>{phaseWord(room.phase)}</span>
                  <span>
                    {room.seated === 0
                      ? 'Nobody has joined'
                      : `${room.connected} of ${count(room.seated, 'player')} connected`}{' '}
                    · {room.playerCount}-player kitchen
                  </span>
                  <span className="hint">
                    {count(room.roundsPlayed, 'round')} played · last active {since(room.lastActivityAt, now)}
                  </span>
                  <a className="bar-link" href={`/r/${room.code}`}>
                    Join
                  </a>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="admin-panel">
          <h2>Recorded rounds</h2>
          <p className="hint">
            Replay links work without the password.
            {sessionBatch !== undefined && (
              <>
                {' '}
                This list:{' '}
                <a href={`/replay?${inviteParam(sessionBatch)}`}>/replay?{inviteParam(sessionBatch)}</a>
              </>
            )}
          </p>

          <label className="batch-filter">
            Batch
            <select
              value={sessionBatch ?? ALL_BATCHES}
              onChange={(event) =>
                chooseSessionBatch(event.target.value === ALL_BATCHES ? undefined : event.target.value)
              }
            >
              <option value={ALL_BATCHES}>All batches</option>
              <option value={NO_BATCH}>{NO_BATCH_LABEL}</option>
              {sessionBatches.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>

          <label className="batch-filter">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(event) => setFlaggedOnly(event.target.checked)}
            />{' '}
            Only rounds with missing or incomplete data
          </label>

          {sessions !== null && sessions.length === 0 ? (
            <p className="hint" role="status">
              {sessionBatch === undefined
                ? 'No rounds recorded yet.'
                : `No rounds recorded in ${sessionBatch === NO_BATCH ? `"${NO_BATCH_LABEL}"` : sessionBatch} yet.`}
            </p>
          ) : (
            <ul className="admin-sessions">
              {(sessions ?? [])
                .filter((session) => !flaggedOnly || qualityLine(session).flagged)
                .map((session) => (
                <li key={session.sessionId}>
                  <a className="mono" href={`/replay/${session.sessionId}`}>
                    {session.sessionId}
                  </a>
                  <span>{conditionName(session.condition)}</span>
                  <span
                    className={session.invite ? 'admin-room-batch mono' : 'admin-room-batch hint'}
                    title="The invite code the round was recorded under"
                  >
                    {batchLabel(session)}
                  </span>
                  <span>
                    {count(session.score, 'point')} · {count(session.playerCount, 'player')} ·{' '}
                    {session.reason === 'timer' ? 'Played to the end' : 'Ended early'}
                  </span>
                  <span data-testid="session-consent">{consentLine(session)}</span>
                  <span
                    data-testid="session-quality"
                    className={qualityLine(session).flagged ? 'admin-quality-flagged' : 'hint'}
                  >
                    {qualityLine(session).text}
                    {session.dishes ? ` · ${count(session.dishes.served, 'dish', 'dishes')} served` : ''}
                  </span>
                  <span className="hint">{stamp(session.endedAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>

      <p className="hint">
        <button type="button" className="admin-logout" onClick={() => void logOut().then(() => setGate('out'))}>
          Log out
        </button>
      </p>
    </main>
  )
}
